import { useEffect, useMemo, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import DashboardLayout from "../../examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "../../examples/Navbars/DashboardNavbar";
import { openAppConfirm } from "../../components/AppDialogs";
import { CardBody, CardHeader, CardTitle, Form } from "reactstrap";
import MDButton from "components/MDButton";
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import { EditSubTitlesCall, GetSlideByIdCall } from "../../redux/api/title.api";
import { DeleteFile } from "../../redux/api/upload.api";
import { store } from "../../redux/store";
import { basicURL } from "../../apiManager/APIConfig";
import { useMaterialUIController } from "../../context";
import { useTheme } from "@mui/material/styles";
import { alpha } from "@mui/material/styles";
import { IconButton, TextField, useMediaQuery } from "@mui/material";
import Icon from "@mui/material/Icon";
import CloseIcon from "@mui/icons-material/Close";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import CheckIcon from "@mui/icons-material/Check";
import RelatedModelsPanel from "./components/RelatedModelsPanel";
import AnatomyAnnotationsPanel from "./components/AnatomyAnnotationsPanel";
import AITextAssistantButton from "../../components/AITextAssistantButton";
import "./map.css";

/*
  Per-category brand gradient for the floating description overlay.
  Mirrors `microlab/src/utils/categoryGradient.ts` so a histology
  model viewed on web shows the same purple spine as the mobile app,
  embryology shows cyan, anatomy shows red. Falls back to histology
  for unknown / empty types.
*/
const CATEGORY_GRADIENTS = {
  his: ["#5B4BFF", "#7C3AED", "#A855F7"],
  three_d_model: ["#5B4BFF", "#7C3AED", "#A855F7"],
  histology: ["#5B4BFF", "#7C3AED", "#A855F7"],
  emb: ["#0EA5E9", "#06B6D4", "#22D3EE"],
  emb_3d_model: ["#0EA5E9", "#06B6D4", "#22D3EE"],
  embryology: ["#0EA5E9", "#06B6D4", "#22D3EE"],
  anatomy: ["#DC2626", "#EF4444", "#FB7185"],
};
const gradientForType = (type) =>
  CATEGORY_GRADIENTS[String(type || "").toLowerCase()] ||
  CATEGORY_GRADIENTS.his;
const iconForType = (type) => {
  const t = String(type || "").toLowerCase();
  if (t.startsWith("emb")) return "child_care";
  if (t === "anatomy") return "accessibility_new";
  return "biotech";
};

const PUBLIC_SLIDE_TYPES = [
  "his",
  "emb",
  "three_d_model",
  "emb_3d_model",
  "anatomy",
  "presentation",
];
const CORE_TYPES = [
  "his",
  "emb",
  "three_d_model",
  "emb_3d_model",
  "anatomy",
];
const defaultModelUrl = "https://modelviewer.dev/shared-assets/models/shishkebab.glb";

function ShowThreeD() {
  const fileInputRef = useRef();
  const iframeRef = useRef(null);
  const { id, lang } = useParams();
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [slideData, setSlideData] = useState(null);
  const [showLoader, setShowLoader] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [modelUrl, setModelUrl] = useState(defaultModelUrl);
  const [activeDeleteButton, setActiveDeleteButton] = useState(false);

  const role = store.getState().auth?.role;
  const isAdmin = String(role || "").toLowerCase() === "admin";
  const isPublicSlide = PUBLIC_SLIDE_TYPES.includes(
    slideData?.parentTitle?.type || slideData?.Titles?.type || slideData?.titles?.type || ""
  );
  const canEditSlide = !isPublicSlide || role === "admin";

  /*
    Author-supplied description for this 3D model. Lives on the
    SubTitle row (the slide's parent) as a `body` Localization, which
    is exactly what the inline "Description" editor on the Titles
    list page writes into. We display it here in a floating overlay
    over the iframe so admins (and any non-admin previewing the
    page) see the same context students get on mobile, edit it
    inline, and instantly verify the result without round-tripping
    back to the catalogue editor.
  */
  const subtitleId = slideData?.parentTitle?.id || null;
  const subtitleType = slideData?.parentTitle?.type || "";
  const parentTitleLocalizations = useMemo(() => {
    const direct =
      slideData?.parentTitle?.titleLocalizations ||
      slideData?.parentTitle?.title_localizations ||
      [];
    return Array.isArray(direct) ? direct : [];
  }, [slideData]);
  const remoteDescription = useMemo(() => {
    const row = parentTitleLocalizations.find((e) => e?.field === "body");
    return (row?.value || "").trim();
  }, [parentTitleLocalizations]);
  const remoteSubtitleTitle = useMemo(() => {
    const row = parentTitleLocalizations.find((e) => e?.field === "title");
    return (row?.value || "").trim();
  }, [parentTitleLocalizations]);
  /*
    Local override so an inline edit can update the overlay copy
    immediately without waiting for a full slide re-fetch. Reset
    whenever the source description changes (e.g. a sibling tab on
    the same browser saved a new value and we re-mount).
  */
  const [descOverride, setDescOverride] = useState(null);
  useEffect(() => {
    setDescOverride(null);
  }, [remoteDescription]);
  const description =
    descOverride != null ? descOverride : remoteDescription;
  const directTitleType =
    slideData?.parentTitle?.type || slideData?.Titles?.type || slideData?.titles?.type || "";
  const rootTitleType = slideData?.parentTitle?.parentTitle?.type || "";
  const resolvedCoreType = CORE_TYPES.includes(directTitleType)
    ? directTitleType
    : CORE_TYPES.includes(rootTitleType)
      ? rootTitleType
      : "";
  const sourceTitleId =
    slideData?.parentTitle?.id || slideData?.Titles?.id || slideData?.titles?.id || null;

  // Fetch slide and get model URL
  useEffect(() => {
    let mounted = true;
    const fetchSlide = async () => {
      try {
        const params = new URLSearchParams(window.location.search);
        const includePrivatePins = params.get("private") === "1";
        const res = await GetSlideByIdCall({ id, lang, includePrivatePins });
        if (!mounted) return;
        // Handle error response (e.g. 404/500) – body may have status or just message, no slide id
        const isError =
          (res && typeof res.status === "number" && res.status >= 400) ||
          (res?.message && res?.id == null);
        if (isError) {
          setSlideData(null);
          setModelUrl(defaultModelUrl);
          setActiveDeleteButton(false);
          return;
        }
        const slide = res?.data ?? res;
        setSlideData(slide);
        const files = slide?.Files ?? slide?.files ?? [];
        const first = Array.isArray(files) ? files[0] : null;
        let url = first?.url ?? first?.path ?? null;
        if (url != null && typeof url === "string") {
          const trimmed = url.trim();
          if (trimmed) {
            if (!trimmed.startsWith("http://") && !trimmed.startsWith("https://")) {
              const base = (basicURL || "").replace(/\/api\/?$/, "").replace(/\/$/, "");
              url = trimmed.startsWith("/") ? base + trimmed : `${base}/${trimmed}`;
            } else {
              url = trimmed;
            }
          } else {
            url = null;
          }
        } else {
          url = null;
        }
        setModelUrl(url || defaultModelUrl);
        setActiveDeleteButton(Boolean(url));
      } catch (e) {
        if (mounted) {
          setSlideData(null);
          setModelUrl(defaultModelUrl);
          setActiveDeleteButton(false);
        }
      }
    };
    fetchSlide();
    return () => { mounted = false; };
  }, [id, lang]);

  // Use iframe so model-viewer runs in its own document (avoids React/custom-element issues).
  // For every 3D-model slide (histology 3D, embryology 3D, anatomy) the iframe runs
  // in "editable" mode so the labeling panel can render hotspots, pick points and
  // drag-to-reposition. Non-3D types stay in pure view mode.
  const LABELABLE_3D_TYPES = ["three_d_model", "emb_3d_model", "anatomy"];
  const is3DLabelable = LABELABLE_3D_TYPES.includes(resolvedCoreType);
  const themeParam = darkMode ? "dark" : "light";
  const editableParam = is3DLabelable ? "&editable=1" : "";
  const iframeSrc =
    typeof window !== "undefined"
      ? `${window.location.origin}/model-viewer.html?src=${encodeURIComponent(modelUrl || defaultModelUrl)}&theme=${themeParam}${editableParam}`
      : "";

  // Handle upload or delete
  const handleAddMaterial = async (event) => {
    if (activeDeleteButton) {
      const file_id = slideData?.Files?.[0]?.id ?? slideData?.files?.[0]?.id;
      const confirmed = await openAppConfirm({
        title: "Delete slide?",
        message: "Are you sure you want to delete this slide? Your pins will remain.",
        confirmText: "Delete",
        confirmColor: "error",
      });
      if (confirmed) {
        await DeleteFile(file_id);
        window.location.reload();
      }
    } else {
      const file = event.target.files[0];
      if (!file) return;

      try {
        setShowLoader(true);
        const formData = new FormData();
        formData.append("file", file);
        formData.append("entity_type", "slide");
        formData.append("entity_id", id);
        formData.append("destination", "slides");
        formData.append("folder_name", "slides");

        const access_token = store.getState().auth?.access_token;
        const headers = { Authorization: access_token };

        const response = await axios.post(`${basicURL}/file/upload`, formData, {
          headers,
          onUploadProgress: (event) => {
            const percent = Math.round((event.loaded / event.total) * 100);
            setUploadProgress(percent);
          },
        });

        setShowLoader(false);
        const glbUrl = `${basicURL}/${response?.data?.extracted_files?.[1]?.path}`;
        setModelUrl(glbUrl);
        window.location.reload();
      } catch (err) {
        console.error("Upload error:", err);
        setShowLoader(false);
      }
    }
  };

  const uploaderTitle =
    uploadProgress !== 100
      ? `Upload Progress: ${uploadProgress}%`
      : "Extracting the zip file, this may take a while...";

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <div className="content">
        <CardHeader>
          <CardTitle tag="h5">
            {canEditSlide && !activeDeleteButton ? (
              <>
                <input
                  type="file"
                  ref={fileInputRef}
                  style={{ display: "none" }}
                  onChange={handleAddMaterial}
                />
                <MDButton
                  color="info"
                  variant="contained"
                  onClick={() => fileInputRef.current.click()}
                  style={{ marginBottom: 15 }}
                >
                  Upload Slide
                </MDButton>
              </>
            ) : canEditSlide && activeDeleteButton ? (
              <MDButton
                color="primary"
                variant="contained"
                onClick={() => handleAddMaterial()}
                style={{ marginBottom: 15 }}
              >
                Delete Model
              </MDButton>
            ) : isPublicSlide ? (
              <span style={{ marginBottom: 15, color: "#666", fontSize: 14 }}>
                View only — only admin can upload or delete public 3D models.
              </span>
            ) : null}
          </CardTitle>
          {showLoader && (
            <CardBody>
              <Form>
                <div>
                  <div>{uploaderTitle}</div>
                  <div style={{ marginBottom: 20, height: 10, backgroundColor: "#ddd" }}>
                    <div
                      style={{
                        height: "100%",
                        width: `${uploadProgress}%`,
                        backgroundColor: "green",
                      }}
                    ></div>
                  </div>
                </div>
              </Form>
            </CardBody>
          )}
        </CardHeader>

        <div
          style={{
            display: "flex",
            flexDirection: isMobile ? "column" : "row",
            alignItems: "flex-start",
            gap: 16,
            width: "100%",
          }}
        >
          <div className="container" style={{ padding: 0, maxWidth: "100%", flex: 1 }}>
            {!modelUrl ? (
              <div className="loadingOrErrorView">
                <div>No 3D model found</div>
              </div>
            ) : (
              <div
                style={{
                  position: "relative",
                  width: "100%",
                  height: "80vh",
                  minHeight: 480,
                  borderRadius: 8,
                  overflow: "hidden",
                  background: darkMode ? "#1a1a1a" : "#f5f5f5",
                }}
              >
                <iframe
                  key={modelUrl}
                  ref={iframeRef}
                  src={iframeSrc}
                  title="3D Model viewer"
                  style={{
                    width: "100%",
                    height: "100%",
                    minHeight: 480,
                    border: "none",
                    display: "block",
                  }}
                />

                {/* Floating "About this model" overlay — same UX as
                    the mobile app's `Interactive3DViewerDescription`.
                    Opens by default, closable, and (admin-only)
                    inline-editable so authors can write the blurb
                    while looking at the model rather than tabbing
                    back to the catalogue editor. Renders nothing
                    when there's no description AND the viewer
                    isn't admin (so non-admins never see an empty
                    nudge). */}
                <ModelDescriptionOverlay
                  description={description}
                  fallbackTitle={remoteSubtitleTitle}
                  gradient={gradientForType(resolvedCoreType || subtitleType)}
                  iconName={iconForType(resolvedCoreType || subtitleType)}
                  darkMode={darkMode}
                  canEdit={isAdmin && !!subtitleId}
                  onSave={async (next) => {
                    if (!subtitleId) return;
                    /* Optimistic update so the overlay reflects the
                       new copy instantly. The Titles list page also
                       refreshes via `EditSubTitlesCall`'s built-in
                       refetch when the user navigates back. */
                    setDescOverride(next);
                    await EditSubTitlesCall({
                      id: subtitleId,
                      lang,
                      type: subtitleType || "",
                      body: next,
                    });
                  }}
                />
              </div>
            )}
          </div>
          <div
            style={{
              width: isMobile ? "100%" : 320,
              flexShrink: 0,
              position: isMobile ? "static" : "sticky",
              top: isMobile ? "auto" : 88,
              display: "flex",
              flexDirection: "column",
              gap: 12,
            }}
          >
            {is3DLabelable && slideData?.id && (
              <AnatomyAnnotationsPanel
                slideId={slideData.id}
                slideType={resolvedCoreType}
                iframeRef={iframeRef}
                canEdit={String(role || "").toLowerCase() === "admin" && canEditSlide}
              />
            )}
            <RelatedModelsPanel
              sourceTitleId={sourceTitleId}
              sourceType={resolvedCoreType}
              isAdmin={String(role || "").toLowerCase() === "admin"}
              lang={lang}
              refreshKey={slideData?.id || 0}
              compact={!isMobile}
              minWidth={isMobile ? 0 : 300}
              maxWidth={isMobile ? 1200 : 320}
            />
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}

/*
  ModelDescriptionOverlay
  -----------------------
  Web counterpart of the mobile `Interactive3DViewerDescription`
  component. Gives admins (and curious non-admin viewers) the same
  floating "About this model" card pinned to the top of the 3D
  iframe — but with an extra inline-edit affordance for admins so
  they can write the description while looking at the model.

  Props:
    • description    — string to display (already trimmed by parent)
    • fallbackTitle  — used as the card heading when present (the
                       SubTitle's localised name); falls back to a
                       generic "About this model" string.
    • gradient       — array of three hex colours; matches the
                       per-category brand palette so this card
                       colour-codes against histology / embryology /
                       anatomy the same way the mobile chrome does.
    • iconName       — Material Icons glyph for the small badge.
    • darkMode       — picks translucent dark or light surface tone.
    • canEdit        — when true, renders the pencil button and the
                       inline-edit textarea. Admins only.
    • onSave         — async (newValue) → void. Parent persists.

  The overlay manages its own visibility (open by default, X button
  closes it) and its own draft state during edit. We pass the
  Material UI `IconButton` instances from props rather than rolling
  custom buttons so the rest of the dashboard's icon styling
  (hover, focus, ripple) stays consistent.
*/
const ModelDescriptionOverlay = ({
  description,
  fallbackTitle,
  gradient,
  iconName,
  darkMode,
  canEdit,
  onSave,
}) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(true);
  const [isEditing, setIsEditing] = useState(false);
  const [draft, setDraft] = useState(description || "");
  const [saving, setSaving] = useState(false);

  /* Resync the draft whenever the source description changes (e.g.
     parent re-fetched the slide). Without this the textarea keeps
     showing whatever the user last typed even if they cancelled. */
  useEffect(() => {
    setDraft(description || "");
  }, [description]);

  /* Re-pop the overlay whenever the description content actually
     changes (new model loaded, sibling tab saved a new value). The
     user's manual close stays sticky within the same description
     because `isOpen` only resets when `description` flips. */
  useEffect(() => {
    setIsOpen(true);
  }, [description]);

  /* Don't render at all if there's nothing to show AND we can't
     even edit it. Non-admin viewers on a description-less model
     should just see the iframe. */
  if (!description && !canEdit) return null;
  if (!isOpen) {
    /* When closed, surface a tiny "Show description" button in the
       top-right so the user can re-open without having to refresh.
       This is especially helpful for admins who closed it once and
       want to come back to it. */
    if (!description && !canEdit) return null;
    return (
      <MDBox
        sx={{
          position: "absolute",
          top: 12,
          right: 12,
          zIndex: 5,
        }}
      >
        <IconButton
          onClick={() => setIsOpen(true)}
          size="small"
          sx={{
            backdropFilter: "blur(10px)",
            backgroundColor: darkMode
              ? "rgba(15,23,42,0.85)"
              : "rgba(255,255,255,0.92)",
            border: "1px solid",
            borderColor: darkMode
              ? "rgba(255,255,255,0.10)"
              : "rgba(15,23,42,0.10)",
            boxShadow: theme.shadows[3],
            "&:hover": {
              backgroundColor: darkMode
                ? "rgba(15,23,42,0.95)"
                : "rgba(255,255,255,1)",
            },
          }}
          aria-label="Show model description"
        >
          <Icon fontSize="small" sx={{ color: gradient?.[1] || theme.palette.primary.main }}>
            menu_book
          </Icon>
        </IconButton>
      </MDBox>
    );
  }

  const headingTitle = fallbackTitle || "About this model";
  const gradientCss = `linear-gradient(90deg, ${gradient[0]} 0%, ${gradient[1]} 50%, ${gradient[2]} 100%)`;
  const badgeGradientCss = `linear-gradient(135deg, ${gradient[0]} 0%, ${gradient[1]} 50%, ${gradient[2]} 100%)`;

  const handleStartEdit = () => {
    setDraft(description || "");
    setIsEditing(true);
  };
  const handleCancelEdit = () => {
    setDraft(description || "");
    setIsEditing(false);
  };
  const handleSave = async () => {
    const next = (draft || "").trim();
    if (next === (description || "").trim()) {
      setIsEditing(false);
      return;
    }
    try {
      setSaving(true);
      await onSave?.(next);
      setIsEditing(false);
    } finally {
      setSaving(false);
    }
  };

  return (
    <MDBox
      sx={{
        position: "absolute",
        top: 12,
        left: 12,
        right: 12,
        zIndex: 5,
        display: "flex",
        justifyContent: "center",
        pointerEvents: "none",
      }}
    >
      <MDBox
        sx={{
          width: "100%",
          maxWidth: 720,
          borderRadius: 3,
          overflow: "hidden",
          border: "1px solid",
          borderColor: darkMode
            ? "rgba(255,255,255,0.10)"
            : "rgba(15,23,42,0.10)",
          backgroundColor: darkMode
            ? "rgba(15,23,42,0.92)"
            : "rgba(255,255,255,0.97)",
          backdropFilter: "blur(12px)",
          boxShadow: theme.shadows[6],
          pointerEvents: "auto",
        }}
      >
        {/* Brand-coloured spine: same gradient strip the mobile
            overlay uses, so the two products feel like one. */}
        <MDBox sx={{ height: 4, width: "100%", background: gradientCss }} />

        <MDBox
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            px: 2,
            pt: 1.5,
            pb: 1,
          }}
        >
          <MDBox
            sx={{
              width: 32,
              height: 32,
              borderRadius: 2,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              background: badgeGradientCss,
              color: "#fff",
              flexShrink: 0,
            }}
          >
            <Icon fontSize="small">{iconName}</Icon>
          </MDBox>
          <MDTypography
            variant="subtitle1"
            sx={{
              flex: 1,
              fontWeight: 800,
              letterSpacing: 0.2,
              color: "text.primary",
              minWidth: 0,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {headingTitle}
          </MDTypography>
          {canEdit && !isEditing && (
            <IconButton
              size="small"
              onClick={handleStartEdit}
              aria-label="Edit description"
              sx={{
                color: "text.secondary",
                "&:hover": {
                  color: "primary.main",
                  bgcolor: alpha(theme.palette.primary.main, 0.08),
                },
              }}
            >
              <EditOutlinedIcon fontSize="small" />
            </IconButton>
          )}
          <IconButton
            size="small"
            onClick={() => {
              if (isEditing) handleCancelEdit();
              setIsOpen(false);
            }}
            aria-label="Close description"
            sx={{
              color: "text.secondary",
              "&:hover": {
                color: "error.main",
                bgcolor: alpha(theme.palette.error.main, 0.08),
              },
            }}
          >
            <CloseIcon fontSize="small" />
          </IconButton>
        </MDBox>

        <MDBox sx={{ px: 2, pb: 2 }}>
          {isEditing ? (
            <>
              <TextField
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    handleSave();
                  }
                  if (e.key === "Escape") handleCancelEdit();
                }}
                placeholder="Write a description for this model — students see this above the 3D viewer."
                multiline
                minRows={3}
                maxRows={8}
                fullWidth
                autoFocus
                disabled={saving}
                sx={{
                  "& .MuiInputBase-input": {
                    fontSize: "0.9rem",
                    lineHeight: 1.5,
                  },
                }}
              />
              <MDBox
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  gap: 1,
                  mt: 1,
                }}
              >
                <AITextAssistantButton
                  value={draft}
                  onApply={setDraft}
                  context="description"
                  label="AI description"
                  disabled={saving}
                />
                <MDBox sx={{ display: "flex", justifyContent: "flex-end", gap: 1 }}>
                  <MDButton
                    variant="text"
                    size="small"
                    onClick={handleCancelEdit}
                    disabled={saving}
                    sx={{ textTransform: "none" }}
                  >
                    Cancel
                  </MDButton>
                  <MDButton
                    color="info"
                    variant="contained"
                    size="small"
                    startIcon={<CheckIcon fontSize="small" />}
                    onClick={handleSave}
                    disabled={saving}
                    sx={{ textTransform: "none", fontWeight: 700 }}
                  >
                    {saving ? "Saving…" : "Save"}
                  </MDButton>
                </MDBox>
              </MDBox>
            </>
          ) : description ? (
            <MDTypography
              variant="body2"
              sx={{
                color: darkMode
                  ? "rgba(255,255,255,0.85)"
                  : "rgba(15,23,42,0.85)",
                lineHeight: 1.55,
                whiteSpace: "pre-wrap",
                /* Cap the readable height so a long blurb scrolls
                   inside the card rather than growing into the
                   model. */
                maxHeight: 200,
                overflowY: "auto",
              }}
            >
              {description}
            </MDTypography>
          ) : (
            /* Admin viewing a model with no description yet — surface
               an inviting empty state that doubles as the "click to
               edit" affordance. Non-admins never see this branch
               because the parent bailed out earlier. */
            <MDBox
              onClick={handleStartEdit}
              sx={{
                py: 1.5,
                cursor: "pointer",
                borderRadius: 1.5,
                border: "1px dashed",
                borderColor: darkMode
                  ? "rgba(255,255,255,0.15)"
                  : "rgba(15,23,42,0.15)",
                textAlign: "center",
                "&:hover": {
                  borderColor: gradient?.[1] || theme.palette.primary.main,
                  bgcolor: alpha(gradient?.[1] || theme.palette.primary.main, 0.04),
                },
              }}
            >
              <MDTypography
                variant="caption"
                sx={{
                  color: "text.secondary",
                  fontStyle: "italic",
                  fontSize: "0.85rem",
                }}
              >
                Click to add a description for this model.
              </MDTypography>
            </MDBox>
          )}
        </MDBox>
      </MDBox>
    </MDBox>
  );
};

export default ShowThreeD;
